import { useController, type Control, type FieldPath, type FieldValues } from "react-hook-form";
import { useTranslation } from "react-i18next";

interface TextInputProps<TFieldValues extends FieldValues = FieldValues> {
  name: FieldPath<TFieldValues>;
  control: Control<TFieldValues>;
  label?: React.ReactNode;
  placeholder?: string;
  disabled?: boolean;
  rules?: Parameters<typeof useController<TFieldValues>>[0]["rules"];
  className?: string;
  type?: "text" | "number" | "email" | "password" | "tel" | "url" | "search" | "date" | "time" | "datetime-local" | "month" | "week";
}

export default function TextInput<TFieldValues extends FieldValues = FieldValues>({
  name,
  control,
  label,
  placeholder,
  disabled,
  rules,
  className = "",
  type = "text",
}: TextInputProps<TFieldValues>) {
  const { t } = useTranslation();
  const {
    field,
    fieldState: { error },
  } = useController<TFieldValues>({
    name,
    control,
    rules,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if(type === "number") {
      const value = parseFloat(e.target.value);
      if(isNaN(value)) {
        return;
      }
      field.onChange(value);
      return;
    }
    field.onChange(e.target.value);
  };

  return (
    <div className={className}>
      {label && (
        <label htmlFor={name} className="form-label">
          {label}
        </label>
      )}
      <input
        {...field}
        onChange={handleChange}
        id={name}
        type={type}
        className={`form-control less-rounded text-dark ${error ? "is-invalid" : ""}`}
        placeholder={placeholder}
        disabled={disabled}
      />
      {error && (
        <div className="invalid-feedback d-block">
          {error.message ? t(error.message) : ""}
        </div>
      )}
    </div>
  );
}

